import { inject, Injectable } from '@angular/core';
import {
  AccountCreationResponse,
  AccountCreationResponses,
  AccountCreationSteps,
  CoreAccountsInterface,
  FinancialAccountDetails,
  FinancialAccountsCoreService,
  FinancialAccountsCreationService,
  FinancialAccountsHistoryService,
  FinancialAccountTransaction,
  FinancialAccountTransactionsRequestConfig,
  FinancialAccountTypes,
  TransactionPagination,
} from '@brightside-web/desktop/feature/financial-accounts-core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { GenericResponseCode } from '@brightside-web/desktop/data-access/core-services';
import { Router } from '@angular/router';
import { ServiceInventoryService, ServiceState } from '@brightside-web/desktop/data-access/service-inventory';

export interface ArrayOfSpendingAccounts {
  accounts: SpendingAccount[];
}

interface SingleSpendingAccount {
  account: SpendingAccount;
}

export interface SpendingAccount extends CoreAccountsInterface {
  accountType: FinancialAccountTypes.SPENDING;
}

@Injectable({ providedIn: 'root' })
export class NewSpendingAccountService {

  private _serviceInventory = inject(ServiceInventoryService);

  private _storedAccount: SpendingAccount | undefined;
  private _currentAccountInfo = new BehaviorSubject<SpendingAccount | undefined>(undefined);
  public readonly currentAccountInfo: Observable<SpendingAccount | undefined> = this._currentAccountInfo.asObservable();

  private _accountCreation = new Subject<AccountCreationResponse>();
  public readonly accountCreation: Observable<AccountCreationResponse> = this._accountCreation.asObservable();

  public readonly recentTransactions: Observable<FinancialAccountTransaction[] | undefined> = this.faHistorySvc.recentTransactions;
  public readonly transactionHistory: Observable<TransactionPagination | undefined> = this.faHistorySvc.transactionHistory;
  public readonly hasMoreTransactions: Observable<boolean> = this.faHistorySvc.hasMoreTransactions;

  private _accountDetails = new BehaviorSubject<FinancialAccountDetails | undefined>(undefined);
  public readonly accountDetails: Observable<FinancialAccountDetails | undefined> = this._accountDetails.asObservable();

  private _serviceState = new BehaviorSubject<string | undefined>(undefined);
  public readonly serviceState: Observable<string | undefined> = this._serviceState.asObservable();

  constructor(
    private faCoreServices: FinancialAccountsCoreService,
    private faCreateSSvc: FinancialAccountsCreationService,
    private faHistorySvc: FinancialAccountsHistoryService,
    private router: Router
  ) {}

  getCurrentAccountId(): number | undefined {
    if (this._storedAccount) return this._storedAccount.accountId;
    return undefined;
  }

  getSpendingAccounts(force = false) {
    if (force) this.clearStoredSpending();
    if (!this._storedAccount) {
      this.faCoreServices.getAccountsByType<ArrayOfSpendingAccounts>(FinancialAccountTypes.SPENDING)
        .subscribe({
          next: (resp) => {
             if (resp.payload) {
               this._currentAccountInfo.next(resp.payload.accounts[0]);
               this._storedAccount = resp.payload.accounts[0];
               this._serviceState.next('no-error');
             } else {
               this._currentAccountInfo.next(undefined);
             }
          },
          error: err => {
            this._serviceState.next('error');
          }
        });
    } else {
      setTimeout(() => {
        this._currentAccountInfo.next(this._storedAccount);
      }, 0);
    }
  }

  clearStoredSpending() {
    this._storedAccount = undefined;
  }

  getSpendingById(id: number) {
    this.faCoreServices.getAccountById<SingleSpendingAccount>(FinancialAccountTypes.SPENDING, id);
  }
  getAccountDetails() {
    if (this._storedAccount && !this._accountDetails.value) {
      this.faCoreServices
        .getAccountDetails(FinancialAccountTypes.SPENDING, this._storedAccount.accountId)
        .subscribe((details) => {
          if (details.result.code === GenericResponseCode.SERVICE_OK) {
            this._accountDetails.next(details.payload);
          }
        });
    }
  }

  getSpendingTransactions(callParams?: FinancialAccountTransactionsRequestConfig) {
    if (this._storedAccount) {
      this.faHistorySvc.getTransactions(FinancialAccountTypes.SPENDING, this._storedAccount?.accountId);
    } else {
      this.currentAccountInfo.pipe(take(1)).subscribe((account) => {
        if (account) this.faHistorySvc.getTransactions(FinancialAccountTypes.SPENDING, account.accountId);
      });
      this.getSpendingAccounts();
    }
  }

  getNextSpendingTransactionsPage() {
    if (this._storedAccount) {
      this.faHistorySvc.getNextTransactionsPage(FinancialAccountTypes.SPENDING, this._storedAccount?.accountId);
    }
  }

  createAccount() {
    this.faCreateSSvc.accountCreatedResp.pipe(take(1)).subscribe(
      (resp) => {
      this._accountCreation.next(resp as AccountCreationResponse);
      this.createAccount();
    });

    this.faCreateSSvc
      .createAccount<SingleSpendingAccount>(FinancialAccountTypes.SPENDING)
      .pipe(
        tap((resp) => {
          if (
            resp.result.code === GenericResponseCode.SERVICE_OK ||
            resp.result.code === AccountCreationResponses.ACCOUNT_ALREADY_EXISTS
          ) {
            this._currentAccountInfo.next(resp.payload.account);
            this._storedAccount = resp.payload.account;
            this.onSuccess(resp.result.code);
          }
        }),
        take(1)
      )
      .subscribe((resp) => {
        this._accountCreation.next(resp.result.code as AccountCreationResponse);
      });
  }

  onSuccess(code: string) {
    if (this._storedAccount) {
      this.faCreateSSvc.advanceStep(AccountCreationSteps.CREATE);
      switch (code) {
        case GenericResponseCode.SERVICE_OK:
          this.router.navigate(['accounts', 'spending', 'debit-card', 'shipping'], {
            state: { accountId: this._storedAccount.accountId },
          });
          break;
        case AccountCreationResponses.ACCOUNT_ALREADY_EXISTS:
          this.router.navigate(['accounts', 'spending', 'dashboard']);
          break;
      }
    }
  }
}
